import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
  Bar,
  BarChart,
  Legend,
} from "recharts";
import { PageState } from "../../components/PageState";
import { useAuth } from "../../auth/AuthContext";
import { formatMoneyBR, pct } from "../../utils/format";

type SelectOption = { id: number; label: string };

type ReportMonth = {
  month: number;
  budget: number;
  actual: number;
  variance: number;
  variancePct: number | null;
};

type ReportTotals = {
  budget: number;
  actual: number;
  variance: number;
  variancePct: number | null;
};

type ReportResponse = {
  year: number;
  filters: { accountId: number | null; costCenterId: number | null };
  cutoffMonth: number;
  ytdBudget: number;
  ytdActual: number;
  ytdVariance: number;
  ytdVariancePct: number | null;
  yearBudget: number;
  yearActual: number;
  yearVariance: number;
  yearVariancePct: number | null;
  months: ReportMonth[];
  totals: {
    currentMonth: ReportTotals;
    ytd: ReportTotals;
    yearTotal: ReportTotals;
    prevYear: { currentMonth: ReportTotals; ytd: ReportTotals; yearTotal: ReportTotals };
  };
};

const monthLabels = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];

const normalizeYearInput = (value: string) => value.replace(/\D/g, "").slice(0, 4);
const isValidYear = (value: string) => {
  if (value.length !== 4) return false;
  const year = Number(value);
  return !Number.isNaN(year) && year <= new Date().getFullYear() + 15;
};

const formatMoneyShort = (value: number) => {
  if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (Math.abs(value) >= 1_000) return `${(value / 1_000).toFixed(0)}k`;
  return formatMoneyBR(value);
};

export const BudgetVsActualPage: React.FC = () => {
  const { client } = useAuth();
  const [accounts, setAccounts] = useState<SelectOption[]>([]);
  const [costCenters, setCostCenters] = useState<SelectOption[]>([]);
  const [accountId, setAccountId] = useState<number | "">("");
  const [costCenterId, setCostCenterId] = useState<number | "">("");
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [data, setData] = useState<ReportResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    client.get("/modules/accounts?take=200&skip=0").then(async (res) => {
      if (!res.ok) return;
      const payload = (await res.json()) as { data: Array<{ id: number; code: string; name: string }> };
      setAccounts(payload.data.map((item) => ({ id: item.id, label: `${item.code} - ${item.name}` })));
    });
    client.get("/modules/cost-centers?take=200&skip=0").then(async (res) => {
      if (!res.ok) return;
      const payload = (await res.json()) as { data: Array<{ id: number; code?: string | null; name: string }> };
      setCostCenters(payload.data.map((item) => ({ id: item.id, label: `${item.code ?? ""} ${item.name}`.trim() })));
    });
  }, [client]);

  const loadReport = useCallback(async () => {
    if (!isValidYear(year)) {
      setError("Ano invalido.");
      setLoading(false);
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ year });
    if (accountId) params.set("accountId", String(accountId));
    if (costCenterId) params.set("costCenterId", String(costCenterId));
    const response = await client.get(`/reports/budget-vs-actual?${params.toString()}`);
    if (!response.ok) {
      setError("Falha ao carregar relatorio.");
      setLoading(false);
      return;
    }
    const payload = (await response.json()) as { data?: ReportResponse } | ReportResponse;
    const report = "data" in payload ? payload.data : payload;
    setData(report ?? null);
    setLoading(false);
  }, [accountId, client, costCenterId, year]);

  useEffect(() => { void loadReport(); }, [loadReport]);

  const chartData = useMemo(
    () =>
      (data?.months ?? []).map((item) => ({
        name: monthLabels[item.month - 1],
        Orcado: Number(item.budget.toFixed(2)),
        Realizado: Number(item.actual.toFixed(2)),
        Variancia: Number(item.variance.toFixed(2)),
      })),
    [data]
  );

  return (
    <div className="container py-4">
      <h1 className="mb-3">Orcado vs Realizado</h1>

      <div className="card shadow-sm mb-3" style={{ borderTop: "3px solid #0d6efd" }}>
        <div className="card-body py-2">
          <div className="row g-2 align-items-end">
            <div className="col-md-1">
              <label className="form-label form-label-sm mb-1">Ano</label>
              <input
                className="form-control form-control-sm"
                value={year}
                onChange={(e) => setYear(normalizeYearInput(e.target.value))}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label form-label-sm mb-1">Conta contabil</label>
              <select
                className="form-select form-select-sm"
                value={accountId}
                onChange={(e) => setAccountId(e.target.value === "" ? "" : Number(e.target.value))}
              >
                <option value="">Todas as contas</option>
                {accounts.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label form-label-sm mb-1">Centro de custo</label>
              <select
                className="form-select form-select-sm"
                value={costCenterId}
                onChange={(e) => setCostCenterId(e.target.value === "" ? "" : Number(e.target.value))}
              >
                <option value="">Todos os centros</option>
                {costCenters.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </div>
            <div className="col-auto d-flex align-items-end">
              <button className="btn btn-primary btn-sm" onClick={loadReport}>Atualizar</button>
            </div>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <PageState loading={loading} error={error}>
        {data && (
          <>
            <div className="row g-3 mb-3">
              <div className="col-md-3">
                <div className="card shadow-sm h-100" style={{ borderLeft: "4px solid #6c757d" }}>
                  <div className="card-body">
                    <div className="text-muted small">Orcado YTD</div>
                    <div className="fw-bold fs-5 mt-1">{formatMoneyBR(data.ytdBudget)}</div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card shadow-sm h-100" style={{ borderLeft: "4px solid #0d6efd" }}>
                  <div className="card-body">
                    <div className="text-muted small">Realizado YTD</div>
                    <div className="fw-bold fs-5 mt-1 text-primary">{formatMoneyBR(data.ytdActual)}</div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card shadow-sm h-100" style={{ borderLeft: "4px solid #adb5bd" }}>
                  <div className="card-body">
                    <div className="text-muted small">Orcado Ano</div>
                    <div className="fw-bold fs-5 mt-1">{formatMoneyBR(data.yearBudget)}</div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card shadow-sm h-100" style={{ borderLeft: "4px solid #20c997" }}>
                  <div className="card-body">
                    <div className="text-muted small">Realizado Ano</div>
                    <div className="fw-bold fs-5 mt-1">{formatMoneyBR(data.yearActual)}</div>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div
                  className="card shadow-sm h-100"
                  style={{ borderLeft: `4px solid ${data.ytdVariance >= 0 ? "#198754" : "#dc3545"}` }}
                >
                  <div className="card-body">
                    <div className="text-muted small">Variancia YTD</div>
                    <div className={`fw-bold fs-5 mt-1 ${data.ytdVariance >= 0 ? "text-success" : "text-danger"}`}>
                      {formatMoneyBR(data.ytdVariance)}
                    </div>
                    <small className="text-muted">{pct(data.ytdVariancePct)}</small>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div
                  className="card shadow-sm h-100"
                  style={{ borderLeft: `4px solid ${data.yearVariance >= 0 ? "#198754" : "#dc3545"}` }}
                >
                  <div className="card-body">
                    <div className="text-muted small">Variancia Ano</div>
                    <div className={`fw-bold fs-5 mt-1 ${data.yearVariance >= 0 ? "text-success" : "text-danger"}`}>
                      {formatMoneyBR(data.yearVariance)}
                    </div>
                    <small className="text-muted">{pct(data.yearVariancePct)}</small>
                  </div>
                </div>
              </div>
            </div>

            <div className="row g-3 mb-3">
              <div className="col-lg-7">
                <div className="card shadow-sm h-100">
                  <div className="card-body">
                    <h6 className="card-title">Serie mensal</h6>
                    <ResponsiveContainer width="100%" height={260}>
                      <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                        <YAxis tickFormatter={formatMoneyShort} tick={{ fontSize: 11 }} width={60} />
                        <Tooltip formatter={(value: number) => formatMoneyBR(value)} />
                        <Legend />
                        <Line type="monotone" dataKey="Orcado" stroke="#6c757d" strokeWidth={2} />
                        <Line type="monotone" dataKey="Realizado" stroke="#0d6efd" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
              <div className="col-lg-5">
                <div className="card shadow-sm h-100">
                  <div className="card-body">
                    <h6 className="card-title">Variancia mensal</h6>
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                        <YAxis tickFormatter={formatMoneyShort} tick={{ fontSize: 11 }} width={60} />
                        <Tooltip formatter={(value: number) => formatMoneyBR(value)} />
                        <Bar dataKey="Variancia" fill="#ffc107" radius={[3, 3, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>

            <div className="card shadow-sm">
              <div className="card-body">
                <h6 className="card-title">Tabela mensal</h6>
                <div className="table-responsive">
                  <table className="table table-sm align-middle">
                    <thead>
                      <tr>
                        <th>Mes</th>
                        <th>Orcado</th>
                        <th>Realizado</th>
                        <th>Variancia</th>
                        <th>%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.months.map((item) => (
                        <tr key={item.month}>
                          <td className="fw-semibold">{monthLabels[item.month - 1]}</td>
                          <td>{formatMoneyBR(item.budget)}</td>
                          <td>{formatMoneyBR(item.actual)}</td>
                          <td className={item.variance < 0 ? "text-danger" : ""}>{formatMoneyBR(item.variance)}</td>
                          <td>{pct(item.variancePct)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}
      </PageState>
    </div>
  );
};
